import { Card } from '../types';
import { searchExternalCards, fetchBulkCards, fetchBulkV2Cards, mapHomuraCardToCard, getCardgames } from '../services/supabaseService';
import {
  findGame,
  getGameSlug,
  isGame,
  isOnePieceGame,
  isDigimonGame,
  isPokemonGame,
  isMagicGame,
  isYugiohGame,
  isGundamGame,
  isDragonBallGame,
  isStarWarsGame,
  isVanguardGame,
  isFabGame,
  isSorceryGame,
  isRiftboundGame,
  isUniversusGame,
  isGrandArchiveGame
} from '../constants/games';

export interface ImportFormatOption {
  id: string;
  label: string;
  isAvailable: boolean;
  description: string;
  placeholder: string;
}

export interface ParsedImportCard {
  name: string;
  code?: string;
  cardId?: string;
  setCode?: string;
  cardNum?: string;
  quantity: number;
  section: string;
  rawLine: string;
  detectedGameSlug?: string;
  detectedGameName?: string;
  condition?: string;
  language?: string;
  price?: number;
  finish?: string;
  expansionName?: string;
}

export interface DeckValidationResult {
  isValid: boolean;
  formatId: string;
  formatLabel: string;
  parsedCards: ParsedImportCard[];
  unparsedLines: string[];
  totalCardsCount: number;
  uniqueCardsCount: number;
  sectionsSummary: { section: string; count: number }[];
  warnings: string[];
  errorMessage?: string;
  detectedName?: string;
}

export interface ImportDeckResult {
  success: boolean;
  importedItems: { card: Card; quantity: number; section: string }[];
  notFoundItems: ParsedImportCard[];
  unparsedLines: string[];
  totalCards: number;
  detectedName?: string;
  errorMessage?: string;
}

export const GAME_IMPORT_FORMATS: Record<string, ImportFormatOption[]> = {
  'one-piece': [
    {
      id: 'optcg-sim',
      label: 'OPTCG Sim / Devs (1xOP01-001 / Código)',
      isAvailable: true,
      description: 'Formato padrão exportado pelo simulador oficial ou OPTCG Sim.',
      placeholder: `1xOP01-001\n4xOP01-004\n4xOP01-025\n2xOP01-029\n4xOP01-016`
    },
    {
      id: 'one-piece-id',
      label: 'Lista por ID de Carta (1xOP01-001 / ID)',
      isAvailable: true,
      description: 'Formato quase igual ao de códigos, utilizando os IDs das cartas no Homura (ex: 1xOP01-001 ou 4xOP01-004_p1).',
      placeholder: `1xOP01-001\n4xOP01-004\n4xOP01-025\n2xOP01-029\n4xOP01-016`
    },
    {
      id: 'one-piece-text',
      label: 'Lista Textual (Líder / Personagem / Evento)',
      isAvailable: true,
      description: 'Formato com nomes e seções separadas.',
      placeholder: `Leader\n1 Roronoa Zoro (OP01-001)\n\nCharacter (50)\n4 Curly.Dadan (OP01-004)\n4 Roronoa Zoro (OP01-025)\n\nEvent\n4 Radical Beam (OP01-029)`
    }
  ],
  'pokemon': [
    {
      id: 'ptcgl',
      label: 'Pokémon TCG Live (PTCGL Export)',
      isAvailable: true,
      description: 'Formato padrão copiado diretamente do Pokémon TCG Live.',
      placeholder: `Pokémon: 12\n4 Charmander MEW 4\n2 Charmeleon PAF 8\n3 Charizard ex OBF 125\n1 Pidgeot ex OBF 164\n\nTreinador: 34\n4 Arven SVI 166\n4 Rare Candy PGO 69\n4 Ultra Ball SVI 196\n\nEnergia: 14\n14 Basic Fire Energy SVE 2`
    },
    {
      id: 'pokemoncard-io',
      label: 'PokemonCard.io (1 Artazon sv2-171)',
      isAvailable: true,
      description: 'Formato exportado do site PokémonCard.io com quantidade, nome e código do set.',
      placeholder: `// PokemonCard.io Deck List
1 Artazon sv2-171
4 Battle VIP Pass swsh8-225
2 Boss's Orders swsh9-132
1 Collapsed Stadium swsh9-137
1 Cresselia swsh11-74
2 Fog Crystal swsh6-140
1 Forest Seal Stone swsh12-156
2 Gardevoir swsh6-61
2 Gardevoir ex sv1-86
4 Iono sv2-185
3 Kirlia swsh12-68
1 Kirlia swsh6-60
4 Level Ball swsh5-129
1 Manaphy swsh9-41
1 Mew cel25-11
1 Pal Pad sm5-132
2 Professor's Research swsh45-60
10 Psychic Energy ex13-109
1 Radiant Greninja swsh10-46
3 Ralts swsh10-60
1 Ralts swsh12-67
3 Rare Candy pgo-69
2 Reversal Energy sv2-192
2 Super Rod sv2-188
3 Ultra Ball swsh12pt5-146
1 Worker swsh12-167
1 Zacian V cel25-16`
    },
    {
      id: 'pokemon-text',
      label: 'Lista Simples (Quantidade + Nome)',
      isAvailable: true,
      description: 'Lista em texto com quantidade e nome das cartas.',
      placeholder: `4 Charmander\n2 Charmeleon\n3 Charizard ex\n4 Arven\n4 Rare Candy`
    }
  ],
  'magic': [
    {
      id: 'mtg-arena',
      label: 'MTG Arena / MTGO Export',
      isAvailable: true,
      description: 'Formato padrão exportado do Magic Arena e Magic Online.',
      placeholder: `Deck\n4 Lightning Bolt (M10) 146\n4 Counterspell (MH2) 267\n4 Brainstorm (EMA) 40\n18 Island\n\nSideboard\n2 Duress (M21) 96\n2 Spell Pierce (XLN) 81`
    },
    {
      id: 'mtg-commander',
      label: 'Commander / EDH Format',
      isAvailable: true,
      description: 'Lista para formato Commander com indicação de Comandante.',
      placeholder: `Commander\n1 Atraxa, Praetors' Voice\n\nDeck\n1 Sol Ring\n1 Arcane Signet\n1 Command Tower\n1 Rhystic Study`
    }
  ],
  'yugioh': [
    {
      id: 'yugioh-ydk',
      label: 'Formato YDK / Passcode (YGOPro / EDOPro)',
      isAvailable: true,
      description: 'Arquivo de baralho estruturado com IDs e seções #main, #extra e !side.',
      placeholder: `#created by ...\n#main\n89631139\n89631139\n14558127\n#extra\n44508094\n!side\n10045474`
    },
    {
      id: 'yugioh-text',
      label: 'Lista Textual (Main / Extra / Side)',
      isAvailable: true,
      description: 'Lista com nomes das cartas separadas por seções.',
      placeholder: `Main\n3 Ash Blossom & Joyous Spring\n3 Maxx "C"\n3 Infinite Impermanence\n\nExtra\n1 Divine Arsenal AA-ZEUS - Sky Thunder\n\nSide\n3 Nibiru, the Primal Being`
    }
  ],
  'digimon': [
    {
      id: 'top-deck-builder',
      label: 'Top Deck Builder / Digimon Meta (Array JSON)',
      isAvailable: true,
      description: 'Formato exportado do digimonmeta.com ou tcgtopdecks-hq.com (Array JSON com códigos das cartas).',
      placeholder: `["Exported from digimonmeta.com","ST6-01","ST6-01","ST6-01","ST6-01","BT2-068","BT2-068","BT2-068","BT2-068","EX2-039","EX2-039","EX2-039","EX2-039","ST14-02","ST14-02","ST14-02","ST14-02","ST14-06","ST14-06","ST14-06","ST14-06","P-077","P-077","P-077","P-077","ST14-07","ST14-07","ST14-07","ST14-07","BT10-081","BT10-081","ST14-08","ST14-08","ST14-08","ST14-09","EX2-044","EX2-044","EX2-044","EX2-044","EX2-074","ST14-10","ST14-11","ST14-11","ST14-11","ST14-11","EX2-065","ST10-15","ST10-15","ST14-12","ST14-12","ST14-12","EX2-071","EX2-071","EX2-071","EX2-071"]`
    },
    {
      id: 'digimon-sim',
      label: 'Digimon TCG / DCGO (Código de Cartas)',
      isAvailable: true,
      description: 'Formato com códigos como BT1-001 ou ST1-03.',
      placeholder: `// Digimon Deck List\n4 BT1-001\n4 ST1-03\n4 BT1-010\n4 BT1-025\n2 BT1-085`
    },
    {
      id: 'digimon-text',
      label: 'Lista com Seções (Digitamas / Principal)',
      isAvailable: true,
      description: 'Lista separada por Ovos (Digitamas) e Deck Principal.',
      placeholder: `Eggs\n4 Koromon (BT1-001)\n\nMain\n4 Agumon (BT1-010)\n4 Greymon (BT1-015)\n2 WarGreymon (BT1-025)`
    }
  ],
  'lorcana': [
    {
      id: 'lorcana-pixelborn',
      label: 'Pixelborn / Lorcana Text',
      isAvailable: true,
      description: 'Formato copiado do Pixelborn ou listas textuais de Lorcana.',
      placeholder: `4 Elsa - Spirit of Winter\n4 Ursula - Deceiver\n4 Friends on the Other Side\n4 Madam Mim - Snake\n4 Merlin - Goat`
    }
  ],
  'dragon-ball-fusion': [
    {
      id: 'dragonball-gg',
      label: 'Dragon Ball (Bandai / dragonball.gg / DBS)',
      isAvailable: true,
      description: 'Formato oficial com nome do deck na 1ª linha, líder (qtd 1 presumida) e cartas no formato [qtd] [código] [nome] [(id)].',
      placeholder: `name (STARTERDECK-SON GOKU-[FS01])\nFS01-01 Son Goku(2)\n4 FS01-02 Whis(4)\n4 FS01-03 Master Roshi(5)\n2 FS01-04 Krillin(6)\n4 FS01-05 Shin(7)\n4 FS01-06 Android 17(8)\n4 FS01-07 Android 18(9)\n2 FS01-08 Son Goku(10)\n2 FS01-09 Son Gohan : Adolescence(11)\n4 FS01-10 Tien Shinhan(12)\n4 FS01-11 Piccolo(13)\n2 FS01-12 Beerus(14)\n4 FS01-13 Frieza(15)\n4 FS01-14 Vegeta(16)\n2 FS01-15 Kamehameha(17)\n4 FS01-16 God Kamehameha(18)`
    },
    {
      id: 'top-deck-builder',
      label: 'Top Deck Builder (tcgtopdecks-hq.com / JSON)',
      isAvailable: true,
      description: 'Formato exportado em Array JSON de códigos.',
      placeholder: `["Exported from dragonball.gg","ST01-001","FB08-007","FB08-007","FB08-008","FB08-008","FB08-008","FB08-008"]`
    },
    {
      id: 'dbf-custom',
      label: 'Dragon Ball Sim / Bandai (1x ST01-001 / Códigos)',
      isAvailable: true,
      description: 'Lista em texto com quantidades e códigos das cartas.',
      placeholder: `1x ST01-001\n2x FB08-007\n4x FB08-008\n4x FB08-019\n4x ST01-004`
    }
  ],
  'gundam': [
    {
      id: 'top-deck-builder',
      label: 'Top Deck Builder (tcgtopdecks-hq.com)',
      isAvailable: true,
      description: 'Formato exportado do tcgtopdecks-hq.com (Array JSON com códigos das cartas).',
      placeholder: `["Exported from tcgtopdecks-hq.com","ST01-009","ST01-009","ST01-009","ST01-009","GD01-086","GD01-086","GD01-086","GD01-086","GD01-073","GD01-073","GD01-073","GD01-073","ST04-001","ST04-001","ST04-001","ST02-001","ST02-001","ST02-001","ST02-001","GD01-066","GD01-066","GD01-024","GD01-024","GD01-024","GD01-024","ST04-010","ST04-010","ST04-010","ST04-010","ST02-010","ST02-010","ST02-010","ST02-010","GD01-118","GD01-118","GD01-118","GD01-118","ST04-013","ST04-013","ST01-014","ST01-014","ST04-012","ST04-012","ST04-012","ST04-012","GD01-126","GD01-126","GD01-126","GD01-126","GD01-129"]`
    },
    {
      id: 'gundam-text',
      label: 'Lista de Códigos / Quantidade (4x ST01-009 ou 4 GD01-086)',
      isAvailable: true,
      description: 'Lista em texto com quantidades e códigos das cartas.',
      placeholder: `4x ST01-009\n4x GD01-086\n4x GD01-073\n3x ST04-001\n4x ST02-001\n2x GD01-066\n4x GD01-024\n4x ST04-010\n4x ST02-010\n4x GD01-118\n2x ST04-013\n2x ST01-014\n4x ST04-012\n4x GD01-126\n1x GD01-129`
    }
  ],
  'star-wars': [
    {
      id: 'swu-melee',
      label: 'Melee / Star Wars Unlimited (Pipes e Seções)',
      isAvailable: true,
      description: 'Formato padrão exportado do Melee.gg com seções Leader, Base, MainDeck e Sideboard ("1 | Nome | Subtítulo").',
      placeholder: `Leader\n1 | Sabine Wren | Galvanized Revolutionary\n\nBase\n1 | Energy Conversion Lab\n\nMainDeck\n3 | Medal Ceremony\n3 | SpecForce Soldier\n2 | Rebel Assault\n3 | Alliance X-Wing\n3 | Battlefield Marine\n3 | Green Squadron A-Wing\n3 | Rebel Pathfinder\n3 | Sabine Wren | Explosives Artist\n3 | Echo Base Defender\n3 | Fighters For Freedom\n3 | Fleet Lieutenant\n3 | Red Three | Unstoppable\n3 | Wing Leader\n3 | For A Cause I Believe In\n3 | K-2SO | Cassian's Counterpart\n3 | Steadfast Battalion\n3 | Guerilla Attack Pod\n\nSideboard\n2 | Heroic Sacrifice\n2 | Wolffe | Suspicious Veteran\n3 | Disabling Fang Fighter\n3 | U-Wing Reinforcement`
    },
    {
      id: 'swu-swudb',
      label: 'SWUDB / SWU Sim (1x Nome / Códigos)',
      isAvailable: true,
      description: 'Formato exportado do SWU-DB ou simulador (ex: 1 Sabine Wren (SOR 014) ou 1x Sabine Wren).',
      placeholder: `// Leader\n1 Sabine Wren (SOR 014)\n\n// Base\n1 Energy Conversion Lab (SOR 022)\n\n// Deck\n3 Medal Ceremony (SOR 001)\n3 SpecForce Soldier (SOR 002)\n2 Rebel Assault (SOR 003)\n3 Alliance X-Wing (SOR 004)\n\n// Sideboard\n2 Heroic Sacrifice (SOR 005)`
    },
    {
      id: 'swu-text',
      label: 'Lista de Cartas (1x Nome / 1 Nome)',
      isAvailable: true,
      description: 'Lista em texto simples com quantidades e nomes das cartas.',
      placeholder: `Leader\n1 Sabine Wren\n\nBase\n1 Energy Conversion Lab\n\nMainDeck\n3 Medal Ceremony\n3 SpecForce Soldier\n\nSideboard\n2 Heroic Sacrifice`
    }
  ],
  'fab': [
    {
      id: 'fab-fabrary',
      label: 'FaBrary / FAB-DB (Hero, Arena cards, Deck cards)',
      isAvailable: true,
      description: 'Formato padrão exportado do FaBrary (fabrary.net) e FAB-DB com seções Hero, Arena cards, Deck cards e indicadores de pitch (red/yellow/blue).',
      placeholder: `Name: Tiny Baby Guardian\nHero: Lyath Goldmane, Vile Savant\nFormat: Classic Constructed\n\nArena cards\n1x Arcane Lantern\n1x Arcanite Skullcap\n1x Attention Grabbers\n1x Fyendal's Spring Tunic\n1x Lionclaw Maul\n1x Nullrune Boots\n1x Rampart of the Ram's Head\n1x Snapdragon Scalers\n1x Two-Faced\n\nDeck cards\n3x Command and Conquer (red)\n3x Cruel Ambition (red)\n2x Edge of Their Seats (red)\n3x Exude Confidence (red)\n3x Leave Them Hanging (red)\n3x Minnowism (red)\n1x Oasis Respite (red)\n3x Razor Reflex (red)\n3x Revolting Gesture (red)\n2x Sadistic Scowl (red)\n3x Sigil of Solace (red)\n3x Villainous Pose (red)\n3x Battered, Beaten, and Broken (yellow)\n3x Cut a Long Story Short (yellow)\n3x Short Shrift (yellow)\n3x Spew Obscenities (yellow)\n3x Booze! (blue)\n3x Escalate Violence (blue)\n3x Goon Beatdown (blue)\n3x Imposing Visage (blue)\n1x Overcrowded (blue)\n3x Power Play (blue)\n3x Talk a Big Game (blue)\n3x Tear Asunder (blue)\n3x Two Steps Ahead (blue)\n\nFeito com amor na FaBrary\nVeja o deck completo em https://fabrary.net/decks/01K57524MP6V9BN9A701HBN7A3`
    },
    {
      id: 'fab-text',
      label: 'Lista de Cartas (1x Nome / 1 Nome)',
      isAvailable: true,
      description: 'Lista em texto simples com quantidades e nomes das cartas por seção.',
      placeholder: `Hero\n1 Lyath Goldmane, Vile Savant\n\nArena\n1 Arcanite Skullcap\n1 Fyendal's Spring Tunic\n\nMain\n3 Command and Conquer (red)\n3 Cruel Ambition (red)\n3 Booze! (blue)`
    }
  ],
  'sorcery': [
    {
      id: 'sorcery-cardio',
      label: 'SorceryCard.io (Texto / Qtd Nome slug-codigo)',
      isAvailable: true,
      description: 'Formato exportado do SorceryCard.io (ex: 1 Cave Goblin cave-goblin). Busca realizada pelo nome.',
      placeholder: `// SorceryCard.io Deck List\n1 Cave Goblin cave-goblin\n2 Abaddon Succubus abaddon-succubus\n1 Archangel Raphael archangel-raphael\n3 Babbling Brook babbling-brook\n1 Dark Tower dark-tower\n1 Necromancer necromancer`
    },
    {
      id: 'sorcery-text',
      label: 'Lista de Cartas (1x Nome / 1 Nome)',
      isAvailable: true,
      description: 'Lista em texto simples com quantidades e nomes das cartas.',
      placeholder: `1 Cave Goblin\n2 Abaddon Succubus\n1 Archangel Raphael\n3 Babbling Brook\n1 Dark Tower`
    }
  ],
  'union-arena': [
    {
      id: 'union-custom',
      label: 'Union Arena Format',
      isAvailable: false,
      description: 'Importador para Union Arena.',
      placeholder: ``
    }
  ],
  'vanguard': [
    {
      id: 'vanguard-decklist',
      label: 'Cardfight!! Vanguard (Deck List / Bushiroad / Deck Log)',
      isAvailable: true,
      description: 'Formato padrão com comentário "// Cardfight Vanguard Deck List" e cartas com quantidade e nome. Distribui automaticamente Main Deck, G-Zone e Ride.',
      placeholder: `// Cardfight Vanguard Deck List\n1 Ambush Demon Stealth Dragon, Mandala Ryuou\n1 Ambush Demon Stealth Dragon, Shibarakku Buster\n2 Ambush Demon Stealth Dragon, Shibarakku Viktor\n1 Ambush Demon Stealth Fiend, Hougen Wing\n2 Ambush Demon Stealth Fiend, Ushimitsu Train\n1 Ambush Demon Stealth Rogue, Shirahagino\n1 Ambush Demon Stealth Rogue, Shishiyuzuki\n1 Cosmetic Snowfall, Shirayuki\n4 Fantasy Petal Storm, Shirayuki\n2 Gateway Stealth Rogue, Ataka\n2 Six Flowers of Phantasms, Shirayuki\n4 Stealth Beast, Leaves Mirage\n3 Stealth Dragon, Dual Weapon\n2 Stealth Dragon, Hiden Scroll\n2 Stealth Fiend, Dart Spider\n3 Stealth Fiend, Jakotsu Girl\n4 Stealth Fiend, One-Eyed Nyudo\n4 Stealth Fiend, Rainy Madame\n1 Stealth Fiend, Rokuro Lady\n1 Stealth Fiend, Tamayuki\n2 Stealth Rogue of Revelation, Yasuie\n2 Sweetly Smiling Ice Petal, Shirayuki\n3 Covert Demonic Dragon, Kumadori Dope\n2 Ambush Demon Stealth Rogue, Izushiotome\n2 Ambush Demon Stealth Rogue, Aizen`
    },
    {
      id: 'vanguard-sections',
      label: 'Lista com Seções (Main / Ride / G-Zone)',
      isAvailable: true,
      description: 'Lista de cartas dividida explicitamente por cabeçalhos de seção (Main, Ride, G-Zone).',
      placeholder: `Main\n4 Fantasy Petal Storm, Shirayuki\n4 Stealth Beast, Leaves Mirage\n3 Stealth Dragon, Dual Weapon\n2 Stealth Dragon, Hiden Scroll\n\nG-Zone\n2 Ambush Demon Stealth Dragon, Shibarakku Viktor\n1 Cosmetic Snowfall, Shirayuki\n1 Ambush Demon Stealth Fiend, Hougen Wing`
    }
  ],
  'cardfight-vanguard': [
    {
      id: 'vanguard-decklist',
      label: 'Cardfight!! Vanguard (Deck List / Bushiroad / Deck Log)',
      isAvailable: true,
      description: 'Formato padrão com comentário "// Cardfight Vanguard Deck List" e cartas com quantidade e nome. Distribui automaticamente Main Deck, G-Zone e Ride.',
      placeholder: `// Cardfight Vanguard Deck List\n1 Ambush Demon Stealth Dragon, Mandala Ryuou\n1 Ambush Demon Stealth Dragon, Shibarakku Buster\n2 Ambush Demon Stealth Dragon, Shibarakku Viktor\n1 Ambush Demon Stealth Fiend, Hougen Wing\n2 Ambush Demon Stealth Fiend, Ushimitsu Train\n1 Ambush Demon Stealth Rogue, Shirahagino\n1 Ambush Demon Stealth Rogue, Shishiyuzuki\n1 Cosmetic Snowfall, Shirayuki\n4 Fantasy Petal Storm, Shirayuki\n2 Gateway Stealth Rogue, Ataka\n2 Six Flowers of Phantasms, Shirayuki\n4 Stealth Beast, Leaves Mirage\n3 Stealth Dragon, Dual Weapon\n2 Stealth Dragon, Hiden Scroll\n2 Stealth Fiend, Dart Spider\n3 Stealth Fiend, Jakotsu Girl\n4 Stealth Fiend, One-Eyed Nyudo\n4 Stealth Fiend, Rainy Madame\n1 Stealth Fiend, Rokuro Lady\n1 Stealth Fiend, Tamayuki\n2 Stealth Rogue of Revelation, Yasuie\n2 Sweetly Smiling Ice Petal, Shirayuki\n3 Covert Demonic Dragon, Kumadori Dope\n2 Ambush Demon Stealth Rogue, Izushiotome\n2 Ambush Demon Stealth Rogue, Aizen`
    },
    {
      id: 'vanguard-sections',
      label: 'Lista com Seções (Main / Ride / G-Zone)',
      isAvailable: true,
      description: 'Lista de cartas dividida explicitamente por cabeçalhos de seção (Main, Ride, G-Zone).',
      placeholder: `Main\n4 Fantasy Petal Storm, Shirayuki\n4 Stealth Beast, Leaves Mirage\n3 Stealth Dragon, Dual Weapon\n2 Stealth Dragon, Hiden Scroll\n\nG-Zone\n2 Ambush Demon Stealth Dragon, Shibarakku Viktor\n1 Cosmetic Snowfall, Shirayuki\n1 Ambush Demon Stealth Fiend, Hougen Wing`
    }
  ],
  'riftbound': [
    {
      id: 'riftbound-piltover',
      label: 'Piltover Archive (piltoverarchive.com / Texto com Seções)',
      isAvailable: true,
      description: 'Formato exportado do Piltover Archive com seções Legend, Champion, MainDeck, Battlefields, Runes e Sideboard.',
      placeholder: `Legend:\n1 Master Yi, Wuju Bladesman\n\nChampion:\n1 Master Yi, Tempered\n\nMainDeck:\n3 Charm\n3 Defy\n3 Discipline\n3 Pit Rookie\n\nBattlefields:\n1 The Arena's Greatest\n1 Emperor's Dais\n1 Seat of Power\n\nRunes:\n7 Body Rune\n5 Calm Rune\n\nSideboard:\n3 Disarming Rake\n2 Alpha Strike`
    },
    {
      id: 'riftbound-text',
      label: 'Lista de Cartas (1x Nome / 1 Nome)',
      isAvailable: true,
      description: 'Lista em texto simples com quantidades e nomes das cartas.',
      placeholder: `1 Master Yi, Wuju Bladesman\n1 Master Yi, Tempered\n3 Charm\n3 Defy\n1 The Arena's Greatest\n7 Body Rune`
    }
  ],
  'universus': [
    {
      id: 'uvs-ultra',
      label: 'UVS Ultra / UniVersus (uvsultra.online / BBCode)',
      isAvailable: true,
      description: 'Formato exportado do uvsultra.online ou fóruns (com tags BBCode ou seções Character, Foundation, Attack, Asset, Action, Side).',
      placeholder: `[b][url=https://uvsultra.online/deck.php?deck=pRfmdavIbbycpqZHFatHaHXAOXfTiC]Kyle G[/url][/b]\n[b]Character[/b]\n1 Midnight\n\n[b]Foundation[/b]\n3 Juicebox Hero\n2 True Identity\n2 Hacker Extraordinaire\n3 Easily Excited\n4 Snack Time\n1 "SPoOoOoOoKY!"\n2 Specialist Of Sound\n3 Profane Sanctuary \n1 Curious Magnetism\n2 Faye-Faye!\n2 Wandering Pirate\n3 Burning with Revenge\n1 Full-On Attack Mode!\n1 Partners in Crimefighting\n4 Orphaned Alchemist\n2 Attitude Selector\n3 Battle Holds the Answer\n1 Nonagon\n\n[b]Attack[/b]\n4 Patchwork Baby\n4 Stairwell Escape Shot\n4 Nut Kracker\n2 Hurricane Upper\n2 Pale Rider\n3 Bladed Uppercut\n2 Bloodsucking Universe\n\n[b]Asset[/b]\n2 Nailed It!!!\n1 Black Bear Diner\n\n[b]Action[/b]\n4 Demon Realm Awakening\n\n[b]Side[/b]\n3 Kuuga (Yuri Chou Upper)\n2 Night Watch\n2 Living Pendulum\n1 Bloodsucking Universe\n2 Nonagon`
    },
    {
      id: 'universus-text',
      label: 'Lista Simples (Quantidade + Nome)',
      isAvailable: true,
      description: 'Lista em texto simples com quantidade e nome das cartas por seção.',
      placeholder: `Character\n1 Midnight\n\nMain\n3 Juicebox Hero\n4 Patchwork Baby\n2 Nailed It!!!\n\nSide\n3 Kuuga (Yuri Chou Upper)`
    }
  ],
  'grand-archive': [
    {
      id: 'grandarchive-omnidex',
      label: 'OmniDex / TCGArchitect (Main, Sideboard, Material)',
      isAvailable: true,
      description: 'Formato padrão exportado do OmniDex (omnidex.gg) e TCGArchitect com seções # Main Deck, # Sideboard e # Material Deck.',
      placeholder: `# Main Deck\n2 Four of Spades\n3 Lurking Assailant\n4 Aesan Protector\n3 Andronika, Eternal Herald\n2 Brisk Windtrotter\n\n# Sideboard\n1 Nullifying Lantern\n1 Viridian Protective Trinket\n2 Extortion Scheme\n\n# Material Deck\n1 Kaze, Spirit of Wind\n1 Lorraine, Wandering Warrior\n1 Backup Charger\n1 Censer of Restful Peace`
    },
    {
      id: 'grandarchive-text',
      label: 'Lista Textual (Quantidade + Nome)',
      isAvailable: true,
      description: 'Lista simples com quantidades e nomes de cartas separadas por seções.',
      placeholder: `Main Deck\n2 Four of Spades\n3 Lurking Assailant\n4 Aesan Protector\n\nSideboard\n1 Nullifying Lantern\n1 Viridian Protective Trinket\n\nMaterial Deck\n1 Kaze, Spirit of Wind\n1 Lorraine, Wandering Warrior`
    }
  ]
};

export function getAvailableFormatsForGame(gameSlug: string): ImportFormatOption[] {
  const canonicalSlug = getGameSlug(gameSlug);
  const formats = GAME_IMPORT_FORMATS[canonicalSlug] ||
                  GAME_IMPORT_FORMATS[gameSlug] ||
                  (canonicalSlug === 'cardfight-vanguard' ? GAME_IMPORT_FORMATS['vanguard'] : undefined) ||
                  (canonicalSlug === 'vanguard' ? GAME_IMPORT_FORMATS['cardfight-vanguard'] : undefined);

  if (formats && formats.length > 0) {
    return formats;
  }

  return [
    {
      id: 'generic-unsupported',
      label: 'Formato do Jogo (Indisponível)',
      isAvailable: false,
      description: 'A importação para esta modalidade ainda está sendo desenvolvida.',
      placeholder: ''
    }
  ];
}

/**
 * ETAPA 1: Validação do formato e parsing sintático das linhas.
 */
export function validateDeckFormat(
  gameSlug: string,
  formatId: string,
  rawText: string
): DeckValidationResult {
  const formats = getAvailableFormatsForGame(gameSlug);
  const selectedFormat = formats.find(f => f.id === formatId) || formats[0];

  if (!selectedFormat || !selectedFormat.isAvailable) {
    return {
      isValid: false,
      formatId: formatId || 'unknown',
      formatLabel: selectedFormat?.label || 'Formato Indisponível',
      parsedCards: [],
      unparsedLines: [],
      totalCardsCount: 0,
      uniqueCardsCount: 0,
      sectionsSummary: [],
      warnings: [],
      errorMessage: 'Função indisponível para este jogo no momento.'
    };
  }

  if (!rawText || !rawText.trim()) {
    return {
      isValid: false,
      formatId: selectedFormat.id,
      formatLabel: selectedFormat.label,
      parsedCards: [],
      unparsedLines: [],
      totalCardsCount: 0,
      uniqueCardsCount: 0,
      sectionsSummary: [],
      warnings: [],
      errorMessage: 'O campo de texto está vazio. Cole a lista do seu baralho.'
    };
  }

  const { parsedCards, unparsedLines, detectedName } = parseRawDeckText(gameSlug, formatId, rawText);

  if (parsedCards.length === 0) {
    return {
      isValid: false,
      formatId: selectedFormat.id,
      formatLabel: selectedFormat.label,
      parsedCards: [],
      unparsedLines,
      totalCardsCount: 0,
      uniqueCardsCount: 0,
      sectionsSummary: [],
      warnings: unparsedLines.length > 0 ? [`${unparsedLines.length} linhas não puderam ser interpretadas.`] : [],
      errorMessage: 'Nenhuma carta reconhecida no texto colado. Verifique a formatação do formato selecionado.',
      detectedName
    };
  }

  // Combine duplicates by code/name/cardId & section
  const combinedCardsMap: Map<string, ParsedImportCard> = new Map();
  for (const item of parsedCards) {
    const key = `${(item.cardId || item.code || item.name).toLowerCase()}__${item.section}`;
    const existing = combinedCardsMap.get(key);
    if (existing) {
      existing.quantity += item.quantity;
    } else {
      combinedCardsMap.set(key, { ...item });
    }
  }

  const uniqueParsedList = Array.from(combinedCardsMap.values());
  const totalCardsCount = uniqueParsedList.reduce((acc, curr) => acc + curr.quantity, 0);

  // Group by section
  const sectionCounts: Record<string, number> = {};
  for (const item of uniqueParsedList) {
    sectionCounts[item.section] = (sectionCounts[item.section] || 0) + item.quantity;
  }
  const sectionsSummary = Object.entries(sectionCounts).map(([section, count]) => ({
    section,
    count
  }));

  const warnings: string[] = [];
  if (unparsedLines.length > 0) {
    warnings.push(`${unparsedLines.length} ${unparsedLines.length === 1 ? 'linha não foi reconhecida' : 'linhas não foram reconhecidas'} e serão ignoradas.`);
  }

  return {
    isValid: true,
    formatId: selectedFormat.id,
    formatLabel: selectedFormat.label,
    parsedCards: uniqueParsedList,
    unparsedLines,
    totalCardsCount,
    uniqueCardsCount: uniqueParsedList.length,
    sectionsSummary,
    warnings,
    detectedName
  };
}

export function isOnePieceLeader(card: any): boolean {
  if (!card) return false;
  const rarity = String(card.rarity || card.set_rarity || '').toUpperCase().trim();
  if (rarity === 'L' || rarity === 'LEADER') return true;

  const cardType = String(card.cardType || card.card_type || card.type || card.category || card.type_name || card.supertype || '').toLowerCase().trim();
  if (cardType === 'leader' || cardType.includes('leader') || cardType.includes('líder')) return true;

  // In One Piece TCG, ONLY leaders have the life stat and no cost stat
  if (card.life !== undefined && card.life !== null && card.life !== '' && Number(card.life) > 0) {
    return true;
  }

  // Name check (e.g. "Roronoa Zoro (L)" or "Monkey.D.Luffy [Leader]")
  const name = String(card.name || '').toLowerCase();
  if (name.includes('(l)') || name.includes('[l]') || name.includes('(leader)') || name.includes('[leader]')) {
    return true;
  }

  return false;
}

export function resolveOnePieceSection(itemSection: string | undefined, card: any): string {
  const normSection = (itemSection || '').trim().toLowerCase();
  if (normSection === 'side' || normSection === 'sideboard') {
    return 'Side';
  }

  const isLeader = isOnePieceLeader(card);
  if (isLeader) {
    return 'Leader';
  }

  // Non-leader cards in One Piece (Character, Event, Stage) MUST go to 'Main'
  return 'Main';
}

export function resolveGameSection(gameSlug: string, itemSection: string | undefined, foundCard: any): string {
  if (!foundCard) return itemSection || 'Main';

  if (isOnePieceGame(gameSlug)) {
    return resolveOnePieceSection(itemSection, foundCard);
  }

  const section = itemSection || 'Main';

  if (isDigimonGame(gameSlug)) {
    const typeStr = String(foundCard.cardType || foundCard.type || foundCard.category || foundCard.kind || '').toLowerCase();
    const rarityStr = String(foundCard.rarity || '').toLowerCase();
    const isEgg = typeStr.includes('egg') || 
                  typeStr.includes('digitama') || 
                  rarityStr.includes('digitama') || 
                  rarityStr.includes('egg');
    if (isEgg && section !== 'Side') {
      return 'Eggs';
    } else if (!isEgg && section === 'Eggs') {
      return 'Main';
    }
    return section;
  }

  if (isYugiohGame(gameSlug)) {
    const typeStr = String(foundCard.type || foundCard.cardType || '').toLowerCase();
    const isExtra = /fusion|synchro|xyz|link/i.test(typeStr);
    if (section === 'Side') {
      return 'Side';
    } else if (section === 'Extra' || isExtra) {
      return 'Extra';
    }
    return 'Main';
  }

  if (isGrandArchiveGame(gameSlug)) {
    if (section === 'Side' || section === 'Sideboard' || section.toLowerCase().includes('side')) {
      return 'Sideboard';
    } else if (section === 'Material' || section === 'Material Deck' || section.toLowerCase().includes('material')) {
      return 'Material Deck';
    } else {
      const typeStr = String(foundCard.cardType || foundCard.card_type || foundCard.type || foundCard.category || foundCard.type_name || foundCard.supertype || '').toLowerCase().trim();
      const costColor = String(foundCard.cost_color || foundCard.costType || foundCard.cost || foundCard.color || '').toLowerCase().trim();
      const isMaterial = typeStr.includes('champion') || typeStr.includes('regalia') || typeStr.includes('campeão') || costColor.includes('memory') || costColor.includes('blue');
      if (isMaterial && section !== 'Sideboard') {
        return 'Material Deck';
      }
      return 'Main Deck';
    }
  }

  if (isGundamGame(gameSlug)) {
    const isResource = /resource|recurso/i.test(String(foundCard.cardType || foundCard.type || foundCard.category || ''));
    if (isResource && section !== 'Side') {
      return 'Resource';
    } else if (!isResource && section === 'Resource') {
      return 'Main';
    }
    return section;
  }

  if (isDragonBallGame(gameSlug)) {
    const typeStr = String(foundCard.cardType || foundCard.type || foundCard.category || foundCard.kind || '').toLowerCase();
    const rarityStr = String(foundCard.rarity || '').toUpperCase();
    const isLeader = typeStr.includes('leader') || 
                     typeStr.includes('líder') || 
                     rarityStr === 'L' || 
                     rarityStr === 'LEADER' ||
                     (foundCard.code && /-[0]*1$/i.test(foundCard.code) && section === 'Leader') ||
                     section === 'Leader';
    if (isLeader && section !== 'Side') {
      return 'Leader';
    } else if (section !== 'Side') {
      return 'Main';
    }
    return section;
  }

  if (isSorceryGame(gameSlug)) {
    const typeStr = String(foundCard.cardType || foundCard.type || foundCard.category || foundCard.kind || '').toLowerCase();
    if (typeStr.includes('avatar') && section !== 'Side') {
      return 'Avatar';
    } else if (typeStr.includes('site') && section !== 'Side') {
      return 'Atlas';
    } else if (section !== 'Side' && section !== 'Avatar' && section !== 'Atlas') {
      return 'Spellbook';
    }
    return section;
  }

  if (isRiftboundGame(gameSlug)) {
    if (section === 'Side') {
      return 'Sideboard';
    }
    if (section === 'Main') {
      const typeStr = String(foundCard.cardType || foundCard.type || foundCard.category || foundCard.kind || '').toLowerCase();
      if (typeStr.includes('legend') || typeStr.includes('lenda')) {
        return 'Legend';
      } else if (typeStr.includes('champion') || typeStr.includes('campeão') || typeStr.includes('campeao')) {
        return 'Champion';
      } else if (typeStr.includes('battlefield') || typeStr.includes('campo')) {
        return 'Battlefields';
      } else if (typeStr.includes('rune') || typeStr.includes('runa')) {
        return 'Runes';
      }
    }
    return section;
  }

  if (isUniversusGame(gameSlug)) {
    if (section === 'Side' || section === 'Sideboard') {
      return 'Side';
    }
    const typeStr = String(foundCard.cardType || foundCard.type || foundCard.category || foundCard.kind || '').toLowerCase();
    if (typeStr.includes('character') || typeStr.includes('leader') || section === 'Character') {
      return 'Character';
    }
    return 'Main';
  }

  if (isStarWarsGame(gameSlug)) {
    if (section === 'Side' || section === 'Sideboard') {
      return 'Sideboard';
    } else if (section === 'Leader' || section === 'Leaders') {
      return 'Leader';
    } else if (section === 'Base' || section === 'Bases') {
      return 'Base';
    } else {
      const typeStr = String(foundCard.cardType || foundCard.type || foundCard.category || foundCard.kind || '').toLowerCase();
      if (typeStr.includes('leader') || typeStr.includes('líder')) {
        return 'Leader';
      } else if (typeStr.includes('base')) {
        return 'Base';
      }
      return 'Main';
    }
  }

  if (isFabGame(gameSlug)) {
    const typeStr = String(foundCard.cardType || foundCard.type || foundCard.card_type || foundCard.category || foundCard.kind || '').toLowerCase();
    if (section === 'Hero' || section === 'Leader' || typeStr.includes('hero') || typeStr.includes('herói') || typeStr.includes('heroi')) {
      return 'Hero';
    } else if (section === 'Arena' || section === 'Equipment' || section === 'Weapons') {
      return 'Arena';
    } else if (section === 'Side' || section === 'Sideboard') {
      return 'Side';
    } else {
      const isEquipOrWeapon = 
        typeStr.includes('equipment') || 
        typeStr.includes('weapon') || 
        typeStr.includes('head') || 
        typeStr.includes('chest') || 
        typeStr.includes('arms') || 
        typeStr.includes('legs') || 
        typeStr.includes('off-hand') ||
        typeStr.includes('equipamento') || 
        typeStr.includes('arma');
      if (isEquipOrWeapon) {
        return 'Arena';
      }
      return 'Main';
    }
  }

  if (isVanguardGame(gameSlug)) {
    const typeStr = String(foundCard.cardType || foundCard.type || foundCard.card_type || foundCard.category || foundCard.kind || '').toLowerCase();
    const gradeStr = String(foundCard.grade || foundCard.stats?.grade || '').toLowerCase();
    if (section === 'G' || section === 'G-Zone' || section === 'G Zone' || section === 'Extra') {
      return 'G';
    } else if (section === 'Ride' || section === 'Ride Deck' || section === 'Ride Line') {
      return 'Ride';
    } else if (
      typeStr.includes('g unit') || 
      typeStr.includes('g-unit') || 
      typeStr.includes('g guardian') || 
      typeStr.includes('g-guardian') || 
      typeStr.includes('stride') || 
      (gradeStr === '4' && !typeStr.includes('normal unit'))
    ) {
      return 'G';
    }
    return 'Main';
  }

  if (isPokemonGame(gameSlug)) {
    return 'Main';
  }

  // Verificação genérica de líder
  const cardRarity = String(foundCard.rarity || foundCard.set_rarity || '').toUpperCase().trim();
  const cardTypeStr = String(foundCard.cardType || foundCard.type || foundCard.card_type || foundCard.category || '').toLowerCase().trim();
  const isLeader = cardRarity === 'L' || 
                   cardRarity === 'LEADER' || 
                   cardTypeStr === 'leader' || 
                   cardTypeStr.includes('leader');
  if (isLeader && section !== 'Side' && section !== 'Sideboard') {
    return 'Leader';
  }

  return section;
}

export function parseRawDeckText(gameSlug: string, formatId: string, rawText: string): { parsedCards: ParsedImportCard[]; unparsedLines: string[]; detectedName?: string } {
  const parsedCards: ParsedImportCard[] = [];
  const unparsedLines: string[] = [];
  let detectedName: string | undefined = undefined;

  const isOnePiece = isOnePieceGame(gameSlug);
  const isGrandArchive = isGrandArchiveGame(gameSlug) || formatId.startsWith('grand') || formatId.includes('omnidex');
  const isDragonBall = isDragonBallGame(gameSlug) || formatId.includes('fusion') || formatId.includes('dragon');
  const isSorcery = isSorceryGame(gameSlug) || formatId.startsWith('sorcery');
  const isRiftbound = isRiftboundGame(gameSlug) || formatId.startsWith('riftbound') || formatId.includes('piltover');
  const isUniversus = isUniversusGame(gameSlug) || formatId.startsWith('uvs') || formatId.startsWith('universus');
  const isStarWars = isStarWarsGame(gameSlug) || formatId.startsWith('swu') || formatId.startsWith('starwars');
  const isFAB = isFabGame(gameSlug) || formatId.startsWith('fab');
  const isVanguard = isVanguardGame(gameSlug) || formatId.startsWith('vanguard') || formatId.startsWith('cfv');
  const isPokemon = isPokemonGame(gameSlug) || formatId.startsWith('pokemon') || formatId.includes('ptcgl') || formatId.includes('pokemoncard');
  let currentSection = isGrandArchive ? 'Main Deck' : (isSorcery ? 'Spellbook' : (isRiftbound ? 'Main' : (isUniversus ? 'Character' : (isStarWars ? 'Leader' : 'Main'))));
  let hasFoundLeaderInOPTCG = false;
  let hasFoundLeaderInDB = false;

  const cleanCardName = (str: string): string => {
    if (!str) return '';
    let res = str
      .replace(/^["']|["']$/g, '')
      .replace(/\s*\(\s*null\s*\)/gi, '')
      .replace(/\s*\[\s*null\s*\]/gi, '')
      .replace(/\s*\(\s*undefined\s*\)/gi, '')
      .replace(/\s*\[\s*undefined\s*\]/gi, '');
    if (!isFAB) {
      res = res
        .replace(/\s*\(\s*(?:null|undefined|\d+|#\d+)\s*\)\s*$/gi, '')
        .replace(/\s*\[\s*(?:null|undefined|\d+|#\d+)\s*\]\s*$/gi, '');
    }
    return res.trim();
  };

  const trimmedRaw = rawText.trim();

  // FORMAT 0: Top Deck Builder / JSON Array Format (e.g. ["Exported from digimonmeta.com","ST6-01",...])
  if (
    (trimmedRaw.startsWith('[') && trimmedRaw.includes(']')) ||
    formatId === 'top-deck-builder' ||
    trimmedRaw.includes('tcgtopdecks-hq.com') ||
    trimmedRaw.includes('tcgtopdecks') ||
    trimmedRaw.includes('digimonmeta.com') ||
    trimmedRaw.includes('digimonmeta') ||
    trimmedRaw.includes('dragonball.gg')
  ) {
    let jsonArray: any[] | null = null;
    
    // Extract everything between first [ and last ]
    const firstBracket = trimmedRaw.indexOf('[');
    const lastBracket = trimmedRaw.lastIndexOf(']');
    if (firstBracket !== -1 && lastBracket !== -1 && lastBracket > firstBracket) {
      const sliceJson = trimmedRaw.slice(firstBracket, lastBracket + 1);
      try {
        const parsed = JSON.parse(sliceJson);
        if (Array.isArray(parsed)) {
          jsonArray = parsed;
        }
      } catch (e) {
        // Fallback: match quoted strings and codes inside brackets
        const inner = sliceJson.slice(1, -1);
        const matches = inner.match(/"([^"]+)"|'([^']+)'|([A-Za-z0-9]+-[A-Za-z0-9]+)/g);
        if (matches && matches.length > 0) {
          jsonArray = matches.map(m => m.replace(/^["']|["']$/g, '').trim());
        }
      }
    }

    if (jsonArray && jsonArray.length > 0) {
      for (const item of jsonArray) {
        if (typeof item === 'string') {
          const itemStr = item.trim();
          if (!itemStr) continue;

          // Skip export watermarks, URLs and headers
          const isMetadata = 
            itemStr.toLowerCase().includes('exported from') ||
            itemStr.toLowerCase().includes('tcgtopdecks') ||
            itemStr.toLowerCase().includes('digimonmeta') ||
            itemStr.toLowerCase().includes('dragonball.gg') ||
            itemStr.toLowerCase().includes('top deck') ||
            itemStr.toLowerCase().includes('http://') ||
            itemStr.toLowerCase().includes('https://') ||
            itemStr.toLowerCase().startsWith('version') ||
            itemStr.toLowerCase().startsWith('deck:');

          if (isMetadata) {
            continue;
          }

          // Check if entry has explicit quantity like "4x ST01-009" or "4 ST01-009"
          const qtyMatch = itemStr.match(/^(\d+)\s*[xX]?\s*([A-Za-z0-9*-]+)(?:\s+(.+))?$/);
          if (qtyMatch && (/^[A-Za-z0-9]+-[A-Za-z0-9]+$/.test(qtyMatch[2]) || qtyMatch[0].includes('x') || qtyMatch[0].includes('X'))) {
            const qty = parseInt(qtyMatch[1], 10);
            const cCode = qtyMatch[2].replace(/\*$/, '').trim();
            const rest = cleanCardName(qtyMatch[3]?.trim() || '');
            parsedCards.push({
              quantity: isNaN(qty) ? 1 : qty,
              code: cCode,
              name: rest || cCode,
              section: 'Main',
              rawLine: itemStr
            });
          } else {
            // Standard single item in array e.g. "ST6-01", "BT2-068", "ST01-009", "GD01-086", "FB08-007"
            parsedCards.push({
              quantity: 1,
              code: itemStr,
              name: itemStr,
              section: 'Main',
              rawLine: itemStr
            });
          }
        } else if (item && typeof item === 'object') {
          const cCode = item.code || item.id || item.number || item.cardNumber;
          const cName = cleanCardName(item.name || cCode);
          const qty = item.quantity || item.qty || item.count || 1;
          const sec = item.section || 'Main';
          if (cCode || cName) {
            parsedCards.push({
              quantity: Number(qty) || 1,
              code: cCode,
              name: cName,
              section: sec,
              rawLine: JSON.stringify(item)
            });
          }
        }
      }

      if (parsedCards.length > 0) {
        return { parsedCards, unparsedLines, detectedName };
      }
    }
  }

  const lines = rawText.split(/\r?\n/);

  for (let i = 0; i < lines.length; i++) {
    const rawLine = lines[i];
    const trimmed = rawLine.trim();
    if (!trimmed) continue;

    // Check for BBCode Deck URL/Name e.g. "[b][url=https://uvsultra.online/deck.php?deck=pRfmdavIbbycpqZHFatHaHXAOXfTiC]Kyle G[/url][/b]"
    const bbcodeUrlMatch = trimmed.match(/\[url=[^\]]+\](.*?)\[\/url\]/i);
    if (bbcodeUrlMatch) {
      const cleanTitle = bbcodeUrlMatch[1].replace(/\[\/?(?:b|i|u|color|size)[^\]]*\]/gi, '').trim();
      if (cleanTitle && !detectedName) {
        detectedName = cleanTitle;
        continue;
      }
    }

    // Strip BBCode formatting (e.g. [b]...[/b], [i]...[/i], etc.)
    const stripped = trimmed.replace(/\[\/?(?:b|i|u|url|color|size)[^\]]*\]/gi, '').trim();
    if (!stripped) continue;

    // Check for FaBrary / standard Deck Name line e.g. "Name: Tiny Baby Guardian" or "name: Son Goten Test" or "deck: Son Goten"
    const deckNameMatch = stripped.match(/^(?:Name|name|deck\s*name|deck|deckname|title|baralho)\s*(?:\(([^)]+)\)|:\s*(.+)|-\s*(.+))$/i);
    if (deckNameMatch) {
      const possibleName = (deckNameMatch[1] || deckNameMatch[2] || deckNameMatch[3] || '').trim();
      if (possibleName && !/^(?:cards|card|list)$/i.test(possibleName)) {
        if (!detectedName) {
          detectedName = possibleName;
        }
        continue;
      }
    }

    // Check for FaBrary / generic Hero header line e.g. "Hero: Lyath Goldmane, Vile Savant" or "Hero: Kayo, Armed and Dangerous"
    const heroLineMatch = stripped.match(/^(?:Hero|Herói|Leader|Líder)\s*:\s*(.+)$/i);
    if (heroLineMatch && heroLineMatch[1]?.trim()) {
      const heroVal = heroLineMatch[1].trim();
      if (!/^(?:cards|card|list)$/i.test(heroVal)) {
        const heroName = cleanCardName(heroVal);
        if (heroName) {
          parsedCards.push({
            quantity: 1,
            name: heroName,
            section: isFAB ? 'Hero' : (isUniversus ? 'Character' : 'Leader'),
            rawLine: trimmed
          });
        }
        continue;
      }
    }

    // Check for Format metadata line e.g. "Format: Classic Constructed" or "Format: Blitz"
    if (/^Format\s*:\s*.+$/i.test(stripped)) {
      continue;
    }

    // Skip FaBrary / FAB-DB watermarks & footers
    if (
      /feito com amor na fabrary/i.test(stripped) ||
      /made with love (?:by|on|at|in) fabrary/i.test(stripped) ||
      /veja o deck completo em/i.test(stripped) ||
      /see the full deck at/i.test(stripped) ||
      /fabrary\.net\/decks\//i.test(stripped) ||
      /fabdb\.net\/decks\//i.test(stripped)
    ) {
      continue;
    }

    // If line 0 is wrapped in parentheses like (STARTERDECK-SON GOKU-[FS01])
    if (i === 0 && !detectedName) {
      const parenNameMatch = stripped.match(/^\(([^)]+)\)$/);
      if (parenNameMatch) {
        detectedName = parenNameMatch[1].trim();
        continue;
      }
    }

    // Skip comments and handle YDK headers
    if (stripped.startsWith('//') || stripped.startsWith('#') || stripped.startsWith('!')) {
      const lower = stripped.toLowerCase().trim();

      // PokemonCard.io header: e.g. "// PokemonCard.io Deck List"
      if (lower.includes('pokemoncard') || (lower.includes('pokemon') && lower.includes('deck list'))) {
        if (!detectedName) {
          detectedName = 'PokemonCard.io Deck';
        }
        continue;
      }

      // Cardfight Vanguard header: e.g. "// Cardfight Vanguard Deck List" or "// Cardfight!! Vanguard Deck List"
      if (lower.includes('vanguard') && (lower.includes('deck') || lower.includes('list'))) {
        if (!detectedName) {
          detectedName = 'Cardfight!! Vanguard Deck';
        }
        continue;
      }

      // Grand Archive sections (OmniDex / TCGArchitect format: # Main Deck, # Sideboard, # Material Deck)
      const cleanHeader = stripped.replace(/^[#/!]+\s*/, '').trim().toLowerCase();
      if (cleanHeader === 'main deck' || cleanHeader === 'main' || cleanHeader === 'maindeck' || cleanHeader === 'main_deck') {
        currentSection = isGrandArchive ? 'Main Deck' : 'Main';
        continue;
      }
      if (cleanHeader === 'material deck' || cleanHeader === 'material' || cleanHeader === 'materialdeck' || cleanHeader === 'material_deck' || cleanHeader === 'champion' || cleanHeader === 'campeão' || cleanHeader === 'campeao') {
        currentSection = isGrandArchive ? 'Material Deck' : 'Main';
        continue;
      }
      if (cleanHeader === 'sideboard' || cleanHeader === 'side' || cleanHeader === 'sidedeck' || cleanHeader === 'side_deck') {
        currentSection = (isStarWars || isGrandArchive) ? 'Sideboard' : 'Side';
        continue;
      }

      // Skip URLs, comment links and web annotations (e.g. # https://tcgarchitect.com/..., // https://omnidex.gg/...)
      if (stripped.includes('http://') || stripped.includes('https://') || stripped.includes('tcgarchitect') || stripped.includes('omnidex') || stripped.includes('fabrary') || stripped.includes('fabdb')) {
        continue;
      }

      // Yugioh specific sections
      if (lower === '#main' || lower === '#maindeck' || lower === '#main_deck') {
        currentSection = 'Main';
        continue;
      }
      if (lower === '#extra' || lower === '#extradeck' || lower === '#extra_deck') {
        currentSection = 'Extra';
        continue;
      }
      if (lower === '!side' || lower === '#side' || lower === '#sideboard' || lower === '#side_deck') {
        currentSection = 'Side';
        continue;
      }

      // Deck name metadata comments (e.g. #name: My Deck, #deckname: My Deck, // name: My Deck, !name: My Deck)
      const nameMatch = stripped.match(/^(?:#|\/\/|!)\s*(?:name|deckname|deck|title|baralho)\s*[:=]\s*(.+)$/i);
      if (nameMatch && nameMatch[1]?.trim() && !detectedName) {
        detectedName = nameMatch[1].trim();
      }

      const secMatch = stripped.match(/^\/\/\s*([A-Za-z0-9\s\-_]+)/);
      if (secMatch) {
        const potentialSec = secMatch[1].trim();
        if (isVanguard) {
          if (/g[\s-_]*zone|g[\s-_]*deck|g[\s-_]*unit|stride|g-guardian|extra/i.test(potentialSec)) {
            currentSection = 'G';
            continue;
          }
          if (/ride[\s-_]*deck|ride[\s-_]*line|ride/i.test(potentialSec)) {
            currentSection = 'Ride';
            continue;
          }
          if (/main[\s-_]*deck|main|trigger|grade|order/i.test(potentialSec)) {
            currentSection = 'Main';
            continue;
          }
        }
        if (/leader|líder/i.test(potentialSec)) currentSection = isUniversus ? 'Character' : (isFAB ? 'Hero' : 'Leader');
        else if (/hero|herói/i.test(potentialSec)) currentSection = isFAB ? 'Hero' : (isUniversus ? 'Character' : 'Leader');
        else if (/character|personagem/i.test(potentialSec)) currentSection = isUniversus ? 'Character' : 'Main';
        else if (/arena|equipment|equipamento|arma|weapon/i.test(potentialSec)) currentSection = isFAB ? 'Arena' : 'Main';
        else if (/base/i.test(potentialSec)) currentSection = 'Base';
        else if (/pokemon|main|spellbook|event|stage|foundation|attack|asset|action|backup/i.test(potentialSec)) currentSection = 'Main';
        else if (/extra/i.test(potentialSec)) currentSection = 'Extra';
        else if (/side/i.test(potentialSec)) currentSection = isStarWars ? 'Sideboard' : 'Side';
        else if (/egg/i.test(potentialSec)) currentSection = 'Eggs';
      }
      continue;
    }

    // Cardfight Vanguard specific section headers (e.g. G Zone, Ride Deck, Main Deck)
    if (isVanguard) {
      const vgH = stripped.toLowerCase().replace(/[:\s\-_]+/g, ' ').trim();
      if (
        vgH === 'g zone' || vgH === 'gzone' || 
        vgH === 'g deck' || vgH === 'gdeck' || 
        vgH === 'g unit' || vgH === 'g units' || 
        vgH === 'g guardian' || vgH === 'g guardians' || 
        vgH === 'stride' || vgH === 'strides' || 
        vgH === 'g' || vgH === 'extra' || vgH === 'extra deck'
      ) {
        currentSection = 'G';
        continue;
      }
      if (
        vgH === 'ride deck' || vgH === 'ridedeck' || 
        vgH === 'ride' || vgH === 'ride line' || 
        vgH === 'rideline'
      ) {
        currentSection = 'Ride';
        continue;
      }
      if (
        vgH === 'main deck' || vgH === 'maindeck' || 
        vgH === 'main' || vgH === 'deck' || 
        vgH === 'triggers' || vgH === 'trigger' || 
        vgH === 'normal units' || vgH === 'normal unit' ||
        vgH === 'grade 3' || vgH === 'grade 2' || 
        vgH === 'grade 1' || vgH === 'grade 0' || 
        vgH === 'orders' || vgH === 'order'
      ) {
        currentSection = 'Main';
        continue;
      }
    }

    // Flesh and Blood specific section headers (e.g. Arena cards, Deck cards, Hero cards, Equipment, Weapons)
    if (isFAB) {
      const fabH = stripped.toLowerCase().replace(/[:\s]+/g, ' ').trim();
      if (
        fabH === 'arena cards' || fabH === 'arena' || 
        fabH === 'equipment' || fabH === 'equipments' || 
        fabH === 'weapons' || fabH === 'weapon' || 
        fabH === 'equipamentos' || fabH === 'armas' || 
        fabH === 'inventory' || fabH === 'inventory cards'
      ) {
        currentSection = 'Arena';
        continue;
      }
      if (
        fabH === 'deck cards' || fabH === 'deck' || 
        fabH === 'main cards' || fabH === 'main deck' || 
        fabH === 'main' || fabH === 'actions' || 
        fabH === 'attack actions' || fabH === 'non-attack actions' || 
        fabH === 'instants'
      ) {
        currentSection = 'Main';
        continue;
      }
      if (fabH === 'hero cards' || fabH === 'hero' || fabH === 'heroi' || fabH === 'herói') {
        currentSection = 'Hero';
        continue;
      }
      if (fabH === 'sideboard' || fabH === 'sideboard cards' || fabH === 'side cards' || fabH === 'side') {
        currentSection = 'Side';
        continue;
      }
    }

    // Star Wars Unlimited specific section headers (e.g. Leader, Base, MainDeck, Sideboard)
    if (isStarWars) {
      const swuH = stripped.toLowerCase().replace(/\s+/g, '');
      if (swuH === 'leader' || swuH.startsWith('leader') || swuH === 'líder' || swuH.startsWith('líder')) {
        currentSection = 'Leader';
        continue;
      } else if (swuH === 'base' || swuH.startsWith('base') || swuH === 'bases') {
        currentSection = 'Base';
        continue;
      } else if (
        swuH === 'maindeck' || swuH.startsWith('maindeck') ||
        swuH === 'main' || swuH.startsWith('main') ||
        swuH === 'deck' || swuH.startsWith('deck')
      ) {
        currentSection = 'Main';
        continue;
      } else if (swuH === 'sideboard' || swuH.startsWith('sideboard') || swuH === 'side' || swuH.startsWith('side')) {
        currentSection = 'Sideboard';
        continue;
      }
    }

    // Universus specific section headers (e.g. Character, Foundation, Attack, Asset, Action, Backup, Arena, Side)
    if (isUniversus) {
      const uvsH = stripped.toLowerCase();
      if (uvsH === 'character' || uvsH.startsWith('character')) {
        currentSection = 'Character';
        continue;
      } else if (
        uvsH === 'foundation' || uvsH.startsWith('foundation') ||
        uvsH === 'attack' || uvsH.startsWith('attack') ||
        uvsH === 'asset' || uvsH.startsWith('asset') ||
        uvsH === 'action' || uvsH.startsWith('action') ||
        uvsH === 'backup' || uvsH.startsWith('backup') ||
        uvsH === 'arena' || uvsH.startsWith('arena') ||
        uvsH === 'main' || uvsH.startsWith('main') ||
        uvsH === 'deck' || uvsH.startsWith('deck')
      ) {
        currentSection = 'Main';
        continue;
      } else if (uvsH === 'side' || uvsH.startsWith('side') || uvsH === 'sideboard' || uvsH.startsWith('sideboard')) {
        currentSection = 'Side';
        continue;
      }
    }

    // Section headers like "Legend:", "Champion:", "MainDeck:", "Battlefields:", "Runes:", "Sideboard:", "Leader", "Leader (1)", "Character (50)", "Deck", "Sideboard (15)", "Commander", "Pokémon: 10"
    const sectionMatch = stripped.match(/^([A-Za-z\s]+)(?::|\s*\(\d+\))?$/);
    if (sectionMatch && !/^\d+/.test(stripped)) {
      const header = sectionMatch[1].trim().toLowerCase();
      if (isRiftbound) {
        if (header.includes('legend') || header.includes('lenda')) {
          currentSection = 'Legend';
          continue;
        } else if (header.includes('champion') || header.includes('campeão') || header.includes('campeao')) {
          currentSection = 'Champion';
          continue;
        } else if (header.includes('battlefield') || header.includes('campo')) {
          currentSection = 'Battlefields';
          continue;
        } else if (header.includes('rune') || header.includes('runa')) {
          currentSection = 'Runes';
          continue;
        } else if (header.includes('sideboard') || header === 'side' || header.includes('side')) {
          currentSection = 'Sideboard';
          continue;
        } else if (header.includes('main') || header.includes('deck') || header.includes('principal')) {
          currentSection = 'Main';
          continue;
        }
      }

      if (isGrandArchive) {
        if (header.includes('material')) {
          currentSection = 'Material Deck';
          continue;
        } else if (header.includes('side')) {
          currentSection = 'Sideboard';
          continue;
        } else if (header.includes('main') || header.includes('deck')) {
          currentSection = 'Main Deck';
          continue;
        }
      }

      if (header.includes('leader') || header.includes('commander') || header.includes('líder') || header.includes('hero') || header.includes('avatar')) {
        currentSection = isUniversus ? 'Character' : 'Leader';
        continue;
      } else if (header === 'base' || header.includes('base') || header === 'bases') {
        currentSection = 'Base';
        continue;
      } else if (header.includes('character') || header.includes('personagem')) {
        currentSection = isUniversus ? 'Character' : 'Main';
        continue;
      } else if (header.includes('foundation') || header.includes('attack') || header.includes('asset') || header.includes('action') || header.includes('backup') || header.includes('arena')) {
        currentSection = 'Main';
        continue;
      } else if (header.includes('sideboard') || header.includes('side')) {
        currentSection = isStarWars ? 'Sideboard' : 'Side';
        continue;
      } else if (header.includes('extra')) {
        currentSection = 'Extra';
        continue;
      } else if (header.includes('egg') || header.includes('digitama')) {
        currentSection = 'Eggs';
        continue;
      } else if (header.includes('deck') || header.includes('event') || header.includes('stage') || header.includes('pokémon') || header.includes('pokemon') || header.includes('trainer') || header.includes('energy') || header.includes('treinador') || header.includes('main') || header.includes('spellbook') || header.includes('principal')) {
        currentSection = 'Main';
        continue;
      }
    }

    let parsedItem: ParsedImportCard | null = null;

    // Format Melee / Pipe Format: "1 | Sabine Wren | Galvanized Revolutionary" or "3 | Medal Ceremony" or "1 | Energy Conversion Lab"
    if (!parsedItem && stripped.includes('|')) {
      const patternMelee = /^(\d+)\s*\|\s*([^|]+?)(?:\s*\|\s*(.+))?$/;
      const matchMelee = stripped.match(patternMelee);
      if (matchMelee) {
        const qty = parseInt(matchMelee[1], 10);
        const cardTitle = cleanCardName(matchMelee[2].trim());
        const cardSubtitle = matchMelee[3] ? cleanCardName(matchMelee[3].trim()) : '';
        const fullName = cardSubtitle ? `${cardTitle} | ${cardSubtitle}` : cardTitle;
        parsedItem = {
          quantity: isNaN(qty) ? 1 : qty,
          name: fullName,
          section: currentSection,
          rawLine: trimmed
        };
      }
    }

    // Format 0 (One Piece by ID): "1xOP01-001" or "4xOP01-004_p1" or "1 OP01-001" or "OP01-001"
    if (!parsedItem && formatId === 'one-piece-id') {
      const matchOpIdWithQty = stripped.match(/^(\d+)\s*[xX]?\s+([A-Za-z0-9*_-]+)(?:\s+(.+))?$/) ||
                               stripped.match(/^(\d+)\s*[xX]\s*([A-Za-z0-9*_-]+)(?:\s+(.+))?$/);
      if (matchOpIdWithQty) {
        const qty = parseInt(matchOpIdWithQty[1], 10);
        const cleanId = matchOpIdWithQty[2].replace(/\*$/, '').trim();
        const rest = cleanCardName(matchOpIdWithQty[3]?.trim() || '');
        parsedItem = {
          quantity: isNaN(qty) ? 1 : qty,
          cardId: cleanId,
          code: cleanId,
          name: rest || cleanId,
          section: currentSection,
          rawLine: trimmed
        };
      } else {
        // Sem quantidade informada (assume 1)
        const matchSingleId = stripped.match(/^([A-Za-z0-9*_-]+)(?:\s+(.+))?$/);
        if (matchSingleId && matchSingleId[1]) {
          const cleanId = matchSingleId[1].replace(/\*$/, '').trim();
          const rest = cleanCardName(matchSingleId[2]?.trim() || '');
          parsedItem = {
            quantity: 1,
            cardId: cleanId,
            code: cleanId,
            name: rest || cleanId,
            section: currentSection,
            rawLine: trimmed
          };
        }
      }
    }

    // Format 1: "1xOP01-001" or "4x OP01-004" or "1x OP01-001 Roronoa Zoro" or "1x Arcane Lantern" or "3x Command and Conquer (red)"
    if (!parsedItem) {
      const patternQtyX = /^(\d+)\s*[xX]\s*([A-Za-z0-9*_-]+)(?:\s+(.+))?$/;
      const matchQtyX = stripped.match(patternQtyX);
      if (matchQtyX) {
        const qty = parseInt(matchQtyX[1], 10);
        const cleanCodeOrFirst = matchQtyX[2].replace(/\*$/, '').trim();
        const rest = cleanCardName(matchQtyX[3]?.trim() || '');
        
        const isCardCode = !isFAB && (
          /^[A-Za-z0-9]+-[A-Za-z0-9]+$/i.test(cleanCodeOrFirst) || 
          /^[A-Z]{2,4}\d{1,4}/i.test(cleanCodeOrFirst) || 
          /^\d{4,10}$/.test(cleanCodeOrFirst)
        );
        
        parsedItem = {
          quantity: isNaN(qty) ? 1 : qty,
          code: isCardCode ? cleanCodeOrFirst : undefined,
          name: isCardCode ? (rest || cleanCodeOrFirst) : (rest ? `${cleanCodeOrFirst} ${rest}` : cleanCodeOrFirst),
          section: currentSection,
          rawLine: trimmed
        };
      }
    }

    // Format 2 (Code First with Qty): "4 FS01-02 Whis(4)" or "4 FB08-007 Son Goku(null)" or "1 OP01-001 Roronoa Zoro" or "4 ST01-004 Son Goku" or "1 D-BT01/001EN Bastion"
    if (!parsedItem) {
      const patternCodeFirst = /^(\d+)\s+([A-Za-z0-9]+-[A-Za-z0-9]+(?:\/[A-Za-z0-9]+)?|[A-Za-z0-9]{2,6}\/[A-Za-z0-9]+)\s+(.+)$/;
      const matchCodeFirst = stripped.match(patternCodeFirst);
      if (matchCodeFirst) {
        const qty = parseInt(matchCodeFirst[1], 10);
        const code = matchCodeFirst[2].trim();
        const name = cleanCardName(matchCodeFirst[3].trim());
        parsedItem = {
          quantity: isNaN(qty) ? 1 : qty,
          name: name || code,
          code,
          section: currentSection,
          rawLine: trimmed
        };
      }
    }

    // Format 2B (Code First without Qty, e.g. Leader): "FS01-01 Son Goku(2)" or "ST01-001 Son Goten(null)" or "OP01-001 Roronoa Zoro"
    if (!parsedItem) {
      const patternCodeNoQty = /^([A-Za-z0-9]+-[A-Za-z0-9]+(?:\/[A-Za-z0-9]+)?|[A-Za-z0-9]{2,6}\/[A-Za-z0-9]+)\s+(.+)$/;
      const matchCodeNoQty = stripped.match(patternCodeNoQty);
      if (matchCodeNoQty) {
        const code = matchCodeNoQty[1].trim();
        const name = cleanCardName(matchCodeNoQty[2].trim());
        parsedItem = {
          quantity: 1,
          code,
          name: name || code,
          section: currentSection,
          rawLine: trimmed
        };
      }
    }

    // Format 3 (Parenthesis Format): "4 Curly.Dadan (OP01-004)" or "1 Roronoa Zoro (OP01-001)" or "3 Kuuga (Yuri Chou Upper)"
    if (!parsedItem) {
      const patternWithParenthesis = /^(\d+)\s+(.+?)\s*\(([^)]+)\)$/;
      const matchParenthesis = stripped.match(patternWithParenthesis);
      if (matchParenthesis) {
        const qty = parseInt(matchParenthesis[1], 10);
        const parenContent = matchParenthesis[3].trim();
        
        // Check if the content inside parenthesis is a genuine card code or just an index / name subtitle
        const isGenuineCode = /^[A-Za-z0-9]+-[A-Za-z0-9]+$/i.test(parenContent) || 
                              (/^[A-Z]{2,4}\d{1,4}/i.test(parenContent) && !/^\d+$/.test(parenContent));

        if (isGenuineCode) {
          const name = cleanCardName(matchParenthesis[2].trim());
          parsedItem = {
            quantity: isNaN(qty) ? 1 : qty,
            name,
            code: parenContent,
            section: currentSection,
            rawLine: trimmed
          };
        } else {
          // If parenthesis was (null), (12), (undefined), treat as card name only without index.
          // If it was a card name subtitle like (Yuri Chou Upper), include it in the card name!
          const isIgnoredParen = parenContent.toLowerCase() === 'null' || 
                                 parenContent.toLowerCase() === 'undefined' || 
                                 /^\d+$/.test(parenContent) || 
                                 /^#\d+$/.test(parenContent);
          const name = isIgnoredParen 
            ? cleanCardName(matchParenthesis[2].trim())
            : cleanCardName(`${matchParenthesis[2].trim()} (${parenContent})`);

          parsedItem = {
            quantity: isNaN(qty) ? 1 : qty,
            name,
            section: currentSection,
            rawLine: trimmed
          };
        }
      }
    }

    // Format 4: PTCGL format like "4 Charmander MEW 4" or "3 Charizard ex OBF 125"
    if (!parsedItem) {
      const patternPTCGL = /^(\d+)\s+(.+?)\s+([A-Z0-9]{3,4})\s+(\d+)$/i;
      const matchPTCGL = stripped.match(patternPTCGL);
      if (matchPTCGL) {
        const qty = parseInt(matchPTCGL[1], 10);
        const name = cleanCardName(matchPTCGL[2].trim());
        const setCode = matchPTCGL[3].trim();
        const cardNum = matchPTCGL[4].trim();
        parsedItem = {
          quantity: isNaN(qty) ? 1 : qty,
          name,
          code: `${setCode}-${cardNum}`,
          setCode,
          cardNum,
          section: currentSection,
          rawLine: trimmed
        };
      }
    }

    // Format 4B (SorceryCard.io format): "1 Cave Goblin cave-goblin" or "2 Abaddon Succubus abaddon-succubus"
    if (!parsedItem && isSorcery) {
      const patternSorcery = /^(\d+)\s+(.+?)\s+([a-z0-9]+(?:-[a-z0-9]+)+)$/;
      const matchSorcery = stripped.match(patternSorcery);
      if (matchSorcery) {
        const qty = parseInt(matchSorcery[1], 10);
        const name = cleanCardName(matchSorcery[2].trim());
        const slugCode = matchSorcery[3].trim();
        parsedItem = {
          quantity: isNaN(qty) ? 1 : qty,
          name: name,
          code: slugCode,
          section: currentSection,
          rawLine: trimmed
        };
      }
    }

    // Format 4C (PokemonCard.io format): "1 Artazon sv2-171" or "4 Battle VIP Pass swsh8-225" or "10 Psychic Energy ex13-109"
    if (!parsedItem && (isPokemon || formatId === 'pokemoncard-io' || formatId === 'ptcgl' || formatId === 'pokemon-text')) {
      const patternPokemonCardIo = /^(\d+)\s*[xX]?\s+(.+?)\s+([a-zA-Z0-9]+-[a-zA-Z0-9]+(?:\/[a-zA-Z0-9]+)?)$/;
      const matchPokemonCardIo = stripped.match(patternPokemonCardIo);
      if (matchPokemonCardIo) {
        const qty = parseInt(matchPokemonCardIo[1], 10);
        const name = cleanCardName(matchPokemonCardIo[2].trim());
        const code = matchPokemonCardIo[3].trim();
        parsedItem = {
          quantity: isNaN(qty) ? 1 : qty,
          name,
          code,
          section: currentSection,
          rawLine: trimmed
        };
      }
    }

    // Format 4D (PokemonCard.io without quantity): "Artazon sv2-171"
    if (!parsedItem && (isPokemon || formatId === 'pokemoncard-io' || formatId === 'ptcgl' || formatId === 'pokemon-text')) {
      const patternPokemonCardIoNoQty = /^(.+?)\s+([a-zA-Z0-9]+-[a-zA-Z0-9]+(?:\/[a-zA-Z0-9]+)?)$/;
      const matchNoQty = stripped.match(patternPokemonCardIoNoQty);
      if (matchNoQty && !/^\d+/.test(stripped)) {
        const name = cleanCardName(matchNoQty[1].trim());
        const code = matchNoQty[2].trim();
        if (name && !/^(pokemon|pokémon|trainer|treinador|energy|energia|main|deck|principal)$/i.test(name)) {
          parsedItem = {
            quantity: 1,
            name,
            code,
            section: currentSection,
            rawLine: trimmed
          };
        }
      }
    }

    // Format 5: "4 Kumacy" or "1 Sol Ring" (Standard quantity + name)
    if (!parsedItem) {
      const patternStandard = /^(\d+)\s+(.+)$/;
      const matchStandard = stripped.match(patternStandard);
      if (matchStandard) {
        const qty = parseInt(matchStandard[1], 10);
        const name = cleanCardName(matchStandard[2].trim());
        const isNumericCode = /^\d{4,10}$/.test(name);
        parsedItem = {
          quantity: isNaN(qty) ? 1 : qty,
          name,
          code: isNumericCode ? name : undefined,
          section: currentSection,
          rawLine: trimmed
        };
      }
    }

    // Format 6: YDK Passcode list (numbers only per line, e.g. "89631139" or "089631139")
    if (!parsedItem && /^\d{4,10}$/.test(stripped)) {
      parsedItem = {
        quantity: 1,
        cardId: stripped,
        code: stripped,
        name: stripped,
        section: currentSection,
        rawLine: trimmed
      };
    }

    // Format 7: Just a card code like "OP01-001" or "BT1-010" or "ST01-001"
    if (!parsedItem && /^[A-Za-z0-9]+-[A-Za-z0-9]+$/i.test(stripped)) {
      parsedItem = {
        quantity: 1,
        code: stripped,
        name: stripped,
        section: currentSection,
        rawLine: trimmed
      };
    }

    if (parsedItem) {
      // Game-specific syntax section refinement during Step 1
      if (isOnePiece) {
        const isLeaderText = /\(L\)$/i.test(parsedItem.name) || 
                             /\[L\]$/i.test(parsedItem.name) || 
                             /leader|líder/i.test(parsedItem.name) ||
                             (parsedItem.code && /-[0]*1$/i.test(parsedItem.code) && parsedItem.quantity === 1 && !hasFoundLeaderInOPTCG);

        if (isLeaderText || currentSection === 'Leader') {
          if (parsedItem.quantity === 1) {
            parsedItem.section = 'Leader';
            hasFoundLeaderInOPTCG = true;
            // After finding a leader under a generic or OPTCG flow, switch default to Main
            if (currentSection === 'Leader') {
              currentSection = 'Main';
            }
          } else {
            // Cards with quantity > 1 can NEVER be One Piece leaders
            parsedItem.section = 'Main';
          }
        } else if (parsedItem.section !== 'Side') {
          parsedItem.section = 'Main';
        }
      } else if (isDragonBall) {
        const isLeaderText = /\(L\)$/i.test(parsedItem.name) || 
                             /\[L\]$/i.test(parsedItem.name) || 
                             /leader|líder/i.test(parsedItem.name) ||
                             (parsedItem.code && /-[0]*1$/i.test(parsedItem.code) && parsedItem.quantity === 1 && !hasFoundLeaderInDB) ||
                             (!hasFoundLeaderInDB && parsedItem.quantity === 1);

        if (isLeaderText || currentSection === 'Leader' || !hasFoundLeaderInDB) {
          if (parsedItem.quantity === 1 || !hasFoundLeaderInDB) {
            parsedItem.quantity = 1;
            parsedItem.section = 'Leader';
            hasFoundLeaderInDB = true;
            if (currentSection === 'Leader') {
              currentSection = 'Main';
            }
          } else {
            parsedItem.section = 'Main';
          }
        } else if (parsedItem.section !== 'Side') {
          parsedItem.section = 'Main';
        }
      } else if (isStarWars) {
        if (parsedItem.section === 'Side' || parsedItem.section === 'Sideboard') {
          parsedItem.section = 'Sideboard';
        } else if (parsedItem.section === 'Leader') {
          parsedItem.section = 'Leader';
        } else if (parsedItem.section === 'Base') {
          parsedItem.section = 'Base';
        } else {
          parsedItem.section = 'Main';
        }
      } else if (isGrandArchive) {
        if (parsedItem.section === 'Side' || parsedItem.section === 'Sideboard' || parsedItem.section.toLowerCase().includes('side')) {
          parsedItem.section = 'Sideboard';
        } else if (parsedItem.section === 'Material' || parsedItem.section === 'Material Deck' || parsedItem.section.toLowerCase().includes('material')) {
          parsedItem.section = 'Material Deck';
        } else {
          parsedItem.section = 'Main Deck';
        }
      } else if (isVanguard) {
        const secLower = (parsedItem.section || '').toLowerCase();
        if (secLower === 'g' || secLower.includes('g-zone') || secLower.includes('g zone') || secLower.includes('stride') || secLower.includes('extra')) {
          parsedItem.section = 'G';
        } else if (secLower === 'ride' || secLower.includes('ride')) {
          parsedItem.section = 'Ride';
        } else {
          parsedItem.section = 'Main';
        }
      }

      parsedCards.push(parsedItem);
    } else {
      unparsedLines.push(trimmed);
    }
  }

  return { parsedCards, unparsedLines, detectedName };
}

/**
 * Busca de cartas em lote otimizada para todos os jogos Homura utilizando os endpoints
 * POST /api/[game]/cards/bulk e POST /api/[game]/cards/bulk/v2.
 * Não faz requisições individuais lentas de fallback: o que foi encontrado entra no deck,
 * e o que não foi é reportado diretamente como erro ao usuário.
 */
async function executeHomuraBulkFetch(
  gameSlug: string,
  parsedCards: ParsedImportCard[],
  onProgress?: (processed: number, total: number, currentCard: string) => void
): Promise<ImportDeckResult> {
  const total = parsedCards.length;
  if (total === 0) {
    return {
      success: true,
      importedItems: [],
      notFoundItems: [],
      unparsedLines: [],
      totalCards: 0
    };
  }

  try {
    // 1. Obter informações do jogo
    const cardGames = await getCardgames();
    const gameData = (cardGames as any[])?.find((g: any) => 
      g.slug === gameSlug || 
      String(g.id) === String(gameSlug) || 
      (g.name && g.name.toLowerCase() === gameSlug.toLowerCase())
    );
    const cardBackUrl = gameData?.card_back_url;
    const gameName = gameData?.name || gameSlug;
    const gameId = gameData?.id || 1;
    const apiSlug = gameData?.slug || (gameSlug === 'cardfight-vanguard' || gameSlug === '11' ? 'cardfight-vanguard' : gameSlug);

    const cleanCode = (code?: string) => {
      if (!code) return '';
      let c = code.replace(/[*#]/g, '').trim();
      if (c.toLowerCase() === 'null' || c.toLowerCase() === 'undefined') return '';
      return c;
    };

    const cleanCardName = (name?: string) => {
      if (!name) return '';
      let n = name
        .replace(/\s*\(\s*null\s*\)/gi, '')
        .replace(/\s*\[\s*null\s*\]/gi, '')
        .replace(/\s*\(\s*undefined\s*\)/gi, '')
        .replace(/\s*\[\s*undefined\s*\]/gi, '')
        .replace(/\s*\(\s*\d+\s*\)$/g, '')
        .replace(/\s*\[\s*\d+\s*\]$/g, '')
        .replace(/\([^)]+\)/g, '')
        .replace(/\[[^\]]+\]/g, '')
        .replace(/\b(leader|líder)\b/gi, '')
        .trim();
      if (n.toLowerCase() === 'null' || n.toLowerCase() === 'undefined') return '';
      return n;
    };

    if (onProgress) {
      onProgress(1, total, `Consultando ${total} cartas no Homura...`);
    }

    const foundCardsMap = new Map<string, Card>();

    const registerCard = (rawCard: any, queryKey?: string) => {
      if (!rawCard) return;
      const cardObj = mapHomuraCardToCard(rawCard, gameName, gameId, cardBackUrl);
      if (!cardObj) return;

      if (queryKey) {
        foundCardsMap.set(queryKey.trim().toUpperCase(), cardObj);
        foundCardsMap.set(queryKey.replace(/[^A-Za-z0-9]/g, '').toUpperCase(), cardObj);
        foundCardsMap.set(`name:${cleanCardName(queryKey).toLowerCase()}`, cardObj);
      }
      if (cardObj.id) {
        foundCardsMap.set(String(cardObj.id).trim().toUpperCase(), cardObj);
        foundCardsMap.set(String(cardObj.id).trim().toLowerCase(), cardObj);
      }
      if (cardObj.code) {
        foundCardsMap.set(cardObj.code.trim().toUpperCase(), cardObj);
        foundCardsMap.set(cardObj.code.replace(/[^A-Za-z0-9]/g, '').toUpperCase(), cardObj);
      }
      if (cardObj.name) {
        foundCardsMap.set(`name:${cardObj.name.trim().toLowerCase()}`, cardObj);
        foundCardsMap.set(`name:${cleanCardName(cardObj.name).toLowerCase()}`, cardObj);
      }
    };

    // 2. Extrair IDs, Códigos, V2 (PTCGL) ou Nomes
    const idList: string[] = [];
    const idSet = new Set<string>();

    for (const item of parsedCards) {
      if (item.cardId) {
        const idTrim = item.cardId.trim();
        if (idTrim && !idSet.has(idTrim.toUpperCase())) {
          idSet.add(idTrim.toUpperCase());
          idList.push(idTrim);
        }
      }
    }

    // 2.1 Se houver IDs (ex: formato 'one-piece-id' ou 'yugioh-ydk'), buscar por ID primeiro
    if (idList.length > 0) {
      if (onProgress) {
        onProgress(Math.min(2, total), total, `Buscando ${idList.length} cartas por ID...`);
      }
      try {
        const bulkIdRes = await fetchBulkCards(apiSlug, 'id', idList);
        if (bulkIdRes && Array.isArray(bulkIdRes.data)) {
          for (const entry of bulkIdRes.data) {
            if (entry.data) {
              registerCard(entry.data, entry.query);
            }
          }
        }
      } catch (e) {
        console.warn('Erro na consulta em lote por ID:', e);
      }
    }

    // 2.2 Verificar PTCGL Pokémon (setCode + cardNum) para uso do /bulk/v2
    const isPokemon = isPokemonGame(apiSlug);
    const ptcglItems = isPokemon ? parsedCards.filter(c => c.setCode && c.cardNum && !foundCardsMap.has((c.cardId || '').toUpperCase()) && !foundCardsMap.has((c.code || '').toUpperCase())) : [];

    if (ptcglItems.length > 0) {
      if (onProgress) {
        onProgress(Math.min(3, total), total, `Buscando ${ptcglItems.length} cartas via Homura Bulk V2...`);
      }
      try {
        const queries = ptcglItems.map(item => ({
          "set.set_code": item.setCode,
          code: { regex: `^0*${item.cardNum}/` }
        }));
        const v2Res = await fetchBulkV2Cards(apiSlug, queries);
        if (v2Res && Array.isArray(v2Res.data)) {
          v2Res.data.forEach((entry, idx) => {
            if (entry.data) {
              const matchedItem = ptcglItems[idx];
              const qKey = matchedItem?.code || `${matchedItem?.setCode}-${matchedItem?.cardNum}`;
              registerCard(entry.data, qKey);
            }
          });
        }
      } catch (e) {
        console.warn('Erro na consulta em lote bulk/v2:', e);
      }
    }

    // 2.3 Extrair códigos únicos das cartas ainda não resolvidas
    const codeList: string[] = [];
    const codeSet = new Set<string>();

    for (const item of parsedCards) {
      const c = cleanCode(item.code);
      if (c) {
        const upper = c.toUpperCase();
        const norm = c.replace(/[^A-Za-z0-9]/g, '').toUpperCase();
        if (!foundCardsMap.has(upper) && !foundCardsMap.has(norm) && !codeSet.has(upper)) {
          codeSet.add(upper);
          codeList.push(c);
        }
      }
    }

    if (codeList.length > 0) {
      if (onProgress) {
        onProgress(Math.min(4, total), total, `Buscando ${codeList.length} cartas por código no Homura...`);
      }
      try {
        const bulkCodeResult = await fetchBulkCards(apiSlug, 'code', codeList);
        if (bulkCodeResult && Array.isArray(bulkCodeResult.data)) {
          for (const entry of bulkCodeResult.data) {
            if (entry.data) {
              registerCard(entry.data, entry.query);
            }
          }
        }
      } catch (e) {
        console.warn('Erro na busca em lote por código:', e);
      }
    }

    // 2.4 Extrair nomes das cartas ainda não resolvidas
    const nameList: string[] = [];
    const nameSet = new Set<string>();

    for (const item of parsedCards) {
      const c = cleanCode(item.code);
      const isFound = (item.cardId && (foundCardsMap.has(item.cardId.toUpperCase()) || foundCardsMap.has(item.cardId.toLowerCase()))) ||
                      (c && (foundCardsMap.has(c.toUpperCase()) || foundCardsMap.has(c.replace(/[^A-Za-z0-9]/g, '').toUpperCase())));

      if (!isFound) {
        const n = cleanCardName(item.name);
        if (n) {
          const lower = n.toLowerCase();
          if (!nameSet.has(lower) && !foundCardsMap.has(`name:${lower}`)) {
            nameSet.add(lower);
            nameList.push(n);
          }
        }
      }
    }

    if (nameList.length > 0) {
      if (onProgress) {
        onProgress(Math.min(Math.floor(total * 0.7), total), total, `Buscando ${nameList.length} cartas por nome no Homura...`);
      }
      try {
        const bulkNameResult = await fetchBulkCards(apiSlug, 'name', nameList);
        if (bulkNameResult && Array.isArray(bulkNameResult.data)) {
          for (const entry of bulkNameResult.data) {
            if (entry.data) {
              registerCard(entry.data, entry.query);
            }
          }
        }
      } catch (e) {
        console.warn('Erro na busca em lote por nome:', e);
      }
    }

    // 3. Associar cada item do parsedCards à carta encontrada e organizar seções pelo resolveGameSection
    const importedItems: { card: Card; quantity: number; section: string }[] = [];
    const notFoundItems: ParsedImportCard[] = [];

    let processed = 0;
    for (const item of parsedCards) {
      processed++;
      const id = item.cardId ? item.cardId.trim() : '';
      const c = cleanCode(item.code);
      const n = cleanCardName(item.name);

      let found: Card | undefined = undefined;
      if (id) {
        found = foundCardsMap.get(id.toUpperCase()) || 
                foundCardsMap.get(id.toLowerCase()) ||
                foundCardsMap.get(id.replace(/[^A-Za-z0-9]/g, '').toUpperCase());
      }
      if (!found && c) {
        found = foundCardsMap.get(c.toUpperCase()) ||
                foundCardsMap.get(c.replace(/[^A-Za-z0-9]/g, '').toUpperCase());
      }
      if (!found && item.setCode && item.cardNum) {
        const ptcglKey = `${item.setCode}-${item.cardNum}`.toUpperCase();
        found = foundCardsMap.get(ptcglKey);
      }
      if (!found && n) {
        found = foundCardsMap.get(`name:${n.toLowerCase()}`);
      }
      if (!found && item.name) {
        found = foundCardsMap.get(`name:${item.name.trim().toLowerCase()}`);
      }

      if (found) {
        const section = resolveGameSection(gameSlug, item.section, found);
        const existingIdx = importedItems.findIndex(
          i => (i.card.id === found!.id || (i.card.code && found!.code && i.card.code === found!.code)) && i.section === section
        );

        if (existingIdx >= 0) {
          importedItems[existingIdx].quantity += item.quantity;
        } else {
          importedItems.push({
            card: found,
            quantity: item.quantity,
            section
          });
        }
      } else {
        notFoundItems.push(item);
      }

      if (onProgress) {
        onProgress(processed, total, item.name || item.code || item.cardId || '');
      }
    }

    const totalCards = importedItems.reduce((acc, curr) => acc + curr.quantity, 0);
    return {
      success: importedItems.length > 0,
      importedItems,
      notFoundItems,
      unparsedLines: [],
      totalCards,
      errorMessage: importedItems.length === 0 ? 'Nenhuma carta foi encontrada no banco de dados com os identificadores fornecidos.' : undefined
    };
  } catch (err) {
    console.warn(`Erro na busca em lote do jogo ${gameSlug}:`, err);
    return {
      success: false,
      importedItems: [],
      notFoundItems: [...parsedCards],
      unparsedLines: [],
      totalCards: 0,
      errorMessage: 'Ocorreu um erro ao consultar as cartas no serviço do jogo.'
    };
  }
}

/**
 * Busca de cartas específica para Magic: The Gathering (Kyubey API), que não possui endpoint de bulk.
 */
async function executeKyubeyFetch(
  gameSlug: string,
  parsedCards: ParsedImportCard[],
  onProgress?: (processed: number, total: number, currentCard: string) => void
): Promise<ImportDeckResult> {
  const importedItems: { card: Card; quantity: number; section: string }[] = [];
  const notFoundItems: ParsedImportCard[] = [];

  let processedCount = 0;
  const total = parsedCards.length;

  const normalizeStr = (s: string) => (s || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
  const normalizeName = (s: string) => (s || '').toLowerCase().replace(/[^a-z0-9]/g, '');

  for (const item of parsedCards) {
    processedCount++;
    if (onProgress) {
      onProgress(processedCount, total, item.name || item.code || '');
    }

    try {
      let foundCard: any = null;
      let cleanCode = item.code ? item.code.replace(/[*#]/g, '').trim() : '';
      if (cleanCode.toLowerCase() === 'null' || cleanCode.toLowerCase() === 'undefined') {
        cleanCode = '';
      }

      let cleanName = item.name ? item.name
        .replace(/\s*\(\s*null\s*\)/gi, '')
        .replace(/\s*\[\s*null\s*\]/gi, '')
        .replace(/\s*\(\s*undefined\s*\)/gi, '')
        .replace(/\s*\[\s*undefined\s*\]/gi, '')
        .replace(/\s*\(\s*\d+\s*\)$/g, '')
        .replace(/\s*\[\s*\d+\s*\]$/g, '')
        .replace(/\([^)]+\)/g, '')
        .replace(/\[[^\]]+\]/g, '')
        .trim() : '';

      if (cleanName.toLowerCase() === 'null' || cleanName.toLowerCase() === 'undefined') {
        cleanName = '';
      }

      const findCodeMatch = (list: any[]) => {
        if (!list || list.length === 0 || !cleanCode) return null;
        const normTargetCode = normalizeStr(cleanCode);
        return list.find((c: any) => {
          const identifiers = [
            normalizeStr(String(c.code || '')),
            normalizeStr(String(c.id || '')),
            normalizeStr(String(c.external_id || '')),
            normalizeStr(String(c.number || '')),
            normalizeStr(String(c.set_code || ''))
          ].filter(Boolean);
          return identifiers.includes(normTargetCode);
        }) || null;
      };

      if (cleanCode) {
        const searchRes = await searchExternalCards(gameSlug, cleanCode, 1, 10);
        foundCard = findCodeMatch(searchRes?.data || []);
      }

      if (!foundCard && cleanName) {
        const searchRes = await searchExternalCards(gameSlug, cleanName, 1, 15);
        const list = searchRes?.data || [];
        const normTargetName = normalizeName(cleanName);
        foundCard = list.find((c: any) => normalizeName(String(c.name || '')) === normTargetName) ||
                    list.find((c: any) => normalizeName(String(c.name || '')).includes(normTargetName)) ||
                    list[0];
      }

      if (foundCard) {
        const section = resolveGameSection(gameSlug, item.section, foundCard);
        const existingIdx = importedItems.findIndex(
          i => (i.card.id === foundCard.id || (i.card.code && foundCard.code && i.card.code === foundCard.code)) && i.section === section
        );

        if (existingIdx >= 0) {
          importedItems[existingIdx].quantity += item.quantity;
        } else {
          importedItems.push({
            card: foundCard,
            quantity: item.quantity,
            section
          });
        }
      } else {
        notFoundItems.push(item);
      }
    } catch (err) {
      console.warn(`Error resolving card ${item.name || item.code}:`, err);
      notFoundItems.push(item);
    }
  }

  const totalCards = importedItems.reduce((acc, curr) => acc + curr.quantity, 0);

  return {
    success: importedItems.length > 0,
    importedItems,
    notFoundItems,
    unparsedLines: [],
    totalCards,
    errorMessage: importedItems.length === 0 ? 'Não foi possível encontrar as cartas no banco de dados do jogo.' : undefined
  };
}

/**
 * ETAPA 2: Busca das cartas validadas na base de dados do jogo.
 * Utiliza busca em lote para todos os jogos Homura (One Piece, Digimon, Dragon Ball,
 * Gundam, Star Wars, Pokémon, Yu-Gi-Oh, Sorcery, Riftbound, UniVersus, FAB, Vanguard).
 * Sem múltiplas requisições nem fallbacks lentos.
 */
export async function executeFetchValidatedCards(
  gameSlug: string,
  parsedCards: ParsedImportCard[],
  onProgress?: (processed: number, total: number, currentCard: string) => void
): Promise<ImportDeckResult> {
  const cardGames = await getCardgames();
  const gameData = (cardGames as any[])?.find((g: any) => 
    g.slug === gameSlug || 
    String(g.id) === String(gameSlug) || 
    (g.name && g.name.toLowerCase() === gameSlug.toLowerCase())
  );

  // Se o jogo for Magic (sauce === 'kyubey'), utiliza o fluxo do Kyubey
  if (gameData?.sauce === 'kyubey') {
    return executeKyubeyFetch(gameSlug, parsedCards, onProgress);
  }

  // Para todos os outros jogos, utiliza a Homura Bulk API (sem requisições individuais!)
  return executeHomuraBulkFetch(gameSlug, parsedCards, onProgress);
}

/**
 * Helper to run full flow directly if needed
 */
export async function executeDeckImport(
  gameSlug: string,
  formatId: string,
  rawText: string,
  onProgress?: (processed: number, total: number, currentCard: string) => void
): Promise<ImportDeckResult> {
  const validation = validateDeckFormat(gameSlug, formatId, rawText);
  if (!validation.isValid) {
    return {
      success: false,
      importedItems: [],
      notFoundItems: [],
      unparsedLines: validation.unparsedLines,
      totalCards: 0,
      errorMessage: validation.errorMessage || 'Falha na validação do formato.'
    };
  }

  const fetchResult = await executeFetchValidatedCards(gameSlug, validation.parsedCards, onProgress);
  return {
    ...fetchResult,
    unparsedLines: validation.unparsedLines
  };
}
